
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { CreditCard, TrendingUp, Calendar, Plus, Minus } from 'lucide-react';

const AppCredits = () => {
  const { user } = useAuth();

  // Fetch credit balance
  const { data: creditBalance } = useQuery({
    queryKey: ['creditBalance', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      const { data, error } = await supabase.rpc('get_credit_balance', {
        user_uuid: user.id
      });
      
      if (error) {
        console.error('Error fetching credit balance:', error);
        return 0;
      }
      
      return data || 0;
    },
    enabled: !!user?.id
  });

  // Fetch credit transactions
  const { data: transactions } = useQuery({
    queryKey: ['creditTransactions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('juv_credits')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) {
        console.error('Error fetching transactions:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id
  });

  const creditValue = (creditBalance || 0) * 50; // $50 per credit
  const earnedCredits = transactions?.filter(t => ['earned', 'rollover', 'bonus', 'referral'].includes(t.transaction_type)).reduce((sum, t) => sum + Number(t.amount), 0) || 0;
  const spentCredits = transactions?.filter(t => t.transaction_type === 'spent').reduce((sum, t) => sum + Number(t.amount), 0) || 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTransactionIcon = (type: string) => {
    return ['earned', 'rollover', 'bonus', 'referral'].includes(type) ? Plus : Minus;
  };

  const getTransactionColor = (type: string) => {
    return ['earned', 'rollover', 'bonus', 'referral'].includes(type) ? 'text-green-600' : 'text-red-600';
  };

  return (
    <AppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-juv-deep-chestnut mb-2">JUV Credits</h1>
          <p className="text-gray-600">Your digital currency for treatments and services</p>
        </div>

        {/* Credit Balance Card */}
        <Card className="bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut text-white">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CreditCard className="w-6 h-6" />
                <h2 className="text-lg font-semibold">You currently have</h2>
              </div>
              <div className="text-4xl font-bold mb-1">{creditBalance || 0}</div>
              <div className="text-lg mb-2">JUV Credits</div>
              <div className="text-white/80 text-sm mb-4">${creditValue} value</div>
              
              <div className="flex gap-3">
                <Button asChild variant="secondary" className="flex-1">
                  <Link to="/app/shop">See What I Can Book</Link>
                </Button>
                <Button asChild variant="outline" className="flex-1 text-white border-white/30 hover:bg-white/10">
                  <Link to="/app/treatments">Book Facial</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Credit Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-juv-deep-chestnut">{earnedCredits}</div>
              <div className="text-xs text-gray-600">Earned</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <Minus className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-juv-deep-chestnut">{spentCredits}</div>
              <div className="text-xs text-gray-600">Redeemed</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <CreditCard className="w-6 h-6 text-juv-terracotta mx-auto mb-2" />
              <div className="text-lg font-semibold text-juv-deep-chestnut">{creditBalance || 0}</div>
              <div className="text-xs text-gray-600">Remaining</div>
            </CardContent>
          </Card>
        </div>

        {/* Transaction Log */}
        <Card>
          <CardHeader>
            <CardTitle className="text-juv-deep-chestnut">Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions && transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((transaction, index) => {
                  const Icon = getTransactionIcon(transaction.transaction_type);
                  const colorClass = getTransactionColor(transaction.transaction_type);
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-4 h-4 ${colorClass}`} />
                        <div>
                          <p className="font-medium text-juv-deep-chestnut">
                            {['earned', 'rollover', 'bonus', 'referral'].includes(transaction.transaction_type) ? '+' : '-'}{transaction.amount} Credits
                          </p>
                          <p className="text-sm text-gray-600">
                            {transaction.description || `${transaction.transaction_type} transaction`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">
                          {formatDate(transaction.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No credit transactions yet</p>
                <p className="text-sm">Start earning credits with treatments!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Action Cards */}
        <div className="grid grid-cols-1 gap-4">
          <Card className="bg-juv-soft-blush">
            <CardContent className="p-4">
              <h3 className="font-semibold text-juv-deep-chestnut mb-2">Save for Smile Makeover</h3>
              <p className="text-sm text-gray-600 mb-3">
                Veneers typically cost 40-60 credits. You're {Math.max(0, 50 - (creditBalance || 0))} credits away!
              </p>
              <Button asChild size="sm" className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                <Link to="/app/treatments/veneers">Learn More</Link>
              </Button>
            </CardContent>
          </Card>
          
          <Card className="bg-juv-rose-clay">
            <CardContent className="p-4">
              <h3 className="font-semibold text-juv-deep-chestnut mb-2">Earn More Credits</h3>
              <p className="text-sm text-gray-600 mb-3">
                Refer a friend and earn 2 bonus credits when they complete their first treatment.
              </p>
              <Button asChild size="sm" variant="outline">
                <Link to="/refer">Refer Now</Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Membership CTA */}
        <Card className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">
              Want to unlock 2 credits/month?
            </h3>
            <p className="text-gray-600 mb-4">
              Join JUV Membership for $99/month and get automatic credit deposits.
            </p>
            <Button asChild className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
              <Link to="/membership">Join for $99 →</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default AppCredits;
